import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { SelectButton } from "primereact/selectbutton";
import { Tag } from "primereact/tag";
import { useMemo, useRef, useState } from "react";
import { OpcUaCertificateDTO } from "../../serviceapi/dataCollectorApi";
import CertificateDetailsDialog from "./CertificateDetailsDialog";
import EnabledTag from "./EnabledTag";
import ThumbprintCell from "./ThumbprintCell";
import {
  OpcUaCertificateKind,
  certificateLabel,
  formatCreatedAt,
  kindLabel,
  kindSeverity,
} from "./opcUaUtils";

interface ApplicationCertificatesTabProps {
  applicationCertificates: OpcUaCertificateDTO[];
  pendingCount: number;
  isLoading: boolean;
  isBusy: boolean;
  fetchCertificates: () => Promise<void>;
  trustCertificate: (certificate: OpcUaCertificateDTO) => Promise<void>;
  unbanCertificate: (certificate: OpcUaCertificateDTO) => Promise<void>;
  revokeCertificate: (certificate: OpcUaCertificateDTO) => Promise<void>;
  deleteCertificate: (certificate: OpcUaCertificateDTO) => Promise<void>;
  downloadCertificate: (certificate: OpcUaCertificateDTO) => Promise<void>;
  uploadTrustedCertificate: (file: File) => Promise<boolean>;
  bulkTrust: (selected: OpcUaCertificateDTO[]) => Promise<void>;
  bulkRevoke: (selected: OpcUaCertificateDTO[]) => Promise<void>;
  bulkDelete: (selected: OpcUaCertificateDTO[]) => Promise<void>;
}

const ALL_KINDS = "ALL";

const KIND_OPTIONS = [
  { label: "All", value: ALL_KINDS },
  { label: "Trusted", value: OpcUaCertificateKind.TRUSTED_APP },
  { label: "Rejected", value: OpcUaCertificateKind.REJECTED },
  { label: "Issuer", value: OpcUaCertificateKind.ISSUER },
];

const ApplicationCertificatesTab = ({
  applicationCertificates,
  pendingCount,
  isLoading,
  isBusy,
  fetchCertificates,
  trustCertificate,
  unbanCertificate,
  revokeCertificate,
  deleteCertificate,
  downloadCertificate,
  uploadTrustedCertificate,
  bulkTrust,
  bulkRevoke,
  bulkDelete,
}: ApplicationCertificatesTabProps) => {
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");
  const [kindFilter, setKindFilter] = useState<string>(ALL_KINDS);
  const [selected, setSelected] = useState<OpcUaCertificateDTO[]>([]);
  const [detailsCertificate, setDetailsCertificate] =
    useState<OpcUaCertificateDTO | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const visibleCertificates = useMemo(
    () =>
      kindFilter === ALL_KINDS
        ? applicationCertificates
        : applicationCertificates.filter(
            (certificate) => certificate.kind === kindFilter
          ),
    [applicationCertificates, kindFilter]
  );

  /**
   * Narrowing the kind filter must not leave rows selected that the operator can no longer
   * see — the bulk buttons would then act on certificates that are off-screen.
   */
  const changeKindFilter = (nextKind: string) => {
    if (nextKind === kindFilter) return;
    setKindFilter(nextKind);
    setSelected([]);
  };

  const onGlobalFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setGlobalFilterValue(value);
    setFilters((prevFilters) => ({
      ...prevFilters,
      global: { ...prevFilters.global, value },
    }));
  };

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    // Reset immediately so selecting the same file twice still fires a change event.
    event.target.value = "";
    if (file) await uploadTrustedCertificate(file);
  };

  const confirmSingle = (
    header: string,
    message: string,
    acceptLabel: string,
    action: () => void,
    danger: boolean
  ) =>
    confirmDialog({
      header,
      message,
      icon: "pi pi-exclamation-triangle",
      acceptLabel,
      rejectLabel: "Cancel",
      acceptClassName: danger ? "p-button-danger" : undefined,
      accept: action,
    });

  const handleApprove = (certificate: OpcUaCertificateDTO) =>
    confirmSingle(
      "Approve certificate",
      `Trust "${certificateLabel(
        certificate
      )}"? The client application will be allowed to connect.`,
      "Approve",
      () => trustCertificate(certificate),
      false
    );

  const handleBan = (certificate: OpcUaCertificateDTO) =>
    confirmSingle(
      "Ban certificate",
      `Ban "${certificateLabel(
        certificate
      )}"? The certificate is revoked and stops working, but the record is kept for audit.`,
      "Ban",
      () => revokeCertificate(certificate),
      true
    );

  const handleUnban = (certificate: OpcUaCertificateDTO) =>
    confirmSingle(
      "Unban certificate",
      `Unban "${certificateLabel(
        certificate
      )}"? The certificate is trusted again and the client application can reconnect.`,
      "Unban",
      () => unbanCertificate(certificate),
      false
    );

  const handleDelete = (certificate: OpcUaCertificateDTO) =>
    confirmSingle(
      "Delete certificate",
      `Delete "${certificateLabel(
        certificate
      )}" entirely? The audit record is removed too, and the client may reappear as rejected on its next connection.`,
      "Delete",
      () => deleteCertificate(certificate),
      true
    );

  const confirmBulk = (
    header: string,
    verb: string,
    acceptLabel: string,
    action: () => void,
    danger: boolean
  ) =>
    confirmDialog({
      header,
      message: `${verb} ${selected.length} selected certificate${
        selected.length === 1 ? "" : "s"
      }?`,
      icon: "pi pi-exclamation-triangle",
      acceptLabel,
      rejectLabel: "Cancel",
      acceptClassName: danger ? "p-button-danger" : undefined,
      accept: action,
    });

  const runBulk = async (operation: (items: OpcUaCertificateDTO[]) => Promise<void>) => {
    const items = [...selected];
    setSelected([]);
    await operation(items);
  };

  const actionBodyTemplate = (certificate: OpcUaCertificateDTO) => (
    <div className="flex justify-content-end gap-1">
      <Button
        className="p-button-text p-button-sm"
        icon="pi pi-eye"
        tooltip="Details"
        onClick={() => setDetailsCertificate(certificate)}
      />
      <Button
        className="p-button-text p-button-sm"
        icon="pi pi-download"
        tooltip="Download .der"
        onClick={() => downloadCertificate(certificate)}
        disabled={!certificate.thumbprint}
      />
      {/*
        One action on the trust axis, never two. A rejected certificate was never trusted,
        so it gets Approve; an active one gets Ban; a revoked one gets Unban. Approve and
        Unban are the same call — showing both would be a duplicate button.
      */}
      {certificate.kind === OpcUaCertificateKind.REJECTED ? (
        <Button
          className="p-button-text p-button-success p-button-sm"
          icon="pi pi-check"
          tooltip="Approve"
          onClick={() => handleApprove(certificate)}
          disabled={!certificate.thumbprint || isBusy}
        />
      ) : certificate.enabled ? (
        <Button
          className="p-button-text p-button-warning p-button-sm"
          icon="pi pi-ban"
          tooltip="Ban (revoke, keeps audit record)"
          onClick={() => handleBan(certificate)}
          disabled={!certificate.thumbprint || isBusy}
        />
      ) : (
        <Button
          className="p-button-text p-button-success p-button-sm"
          icon="pi pi-unlock"
          tooltip="Unban (trust again)"
          onClick={() => handleUnban(certificate)}
          disabled={!certificate.thumbprint || isBusy}
        />
      )}
      <Button
        className="p-button-text p-button-danger p-button-sm"
        icon="pi pi-trash"
        tooltip="Delete"
        onClick={() => handleDelete(certificate)}
        disabled={!certificate.thumbprint || isBusy}
      />
    </div>
  );

  return (
    <div className="flex flex-column gap-3" style={{ height: "100%" }}>
      {pendingCount > 0 && (
        <Message
          severity="warn"
          text={`${pendingCount} certificate${
            pendingCount === 1 ? " is" : "s are"
          } awaiting approval.`}
        />
      )}

      <div className="flex justify-content-between align-items-center gap-2 flex-wrap">
        <div className="flex align-items-center gap-2 flex-wrap">
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search" />
            <InputText
              type="search"
              value={globalFilterValue}
              onChange={onGlobalFilterChange}
              placeholder="Search certificates"
            />
          </IconField>
          <SelectButton
            value={kindFilter}
            options={KIND_OPTIONS}
            onChange={(e) => e.value && changeKindFilter(e.value as string)}
            unselectable={false}
          />
        </div>
        <div className="flex gap-2">
          <Button
            label="Upload trusted"
            icon="pi pi-upload"
            className="p-button-sm"
            onClick={handleUploadClick}
            disabled={isBusy}
            tooltip="Upload a client application certificate and trust it immediately"
          />
          <Button
            className="p-button-text p-button-sm"
            icon="pi pi-refresh"
            tooltip="Refresh"
            onClick={fetchCertificates}
            disabled={isLoading}
          />
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".der,.pem,.crt,.cer"
        style={{ display: "none" }}
        onChange={handleFileSelected}
      />

      {selected.length > 0 && (
        <div className="flex align-items-center gap-2 flex-wrap">
          <span className="font-medium">{selected.length} selected:</span>
          <Button
            label="Approve"
            icon="pi pi-check"
            className="p-button-sm p-button-success"
            disabled={isBusy}
            onClick={() =>
              confirmBulk("Approve certificates", "Trust", "Approve", () =>
                runBulk(bulkTrust), false
              )
            }
          />
          <Button
            label="Ban"
            icon="pi pi-ban"
            className="p-button-sm p-button-warning"
            disabled={isBusy}
            onClick={() =>
              confirmBulk("Ban certificates", "Revoke", "Ban", () =>
                runBulk(bulkRevoke), true
              )
            }
          />
          <Button
            label="Delete"
            icon="pi pi-trash"
            className="p-button-sm p-button-danger"
            disabled={isBusy}
            onClick={() =>
              confirmBulk("Delete certificates", "Permanently delete", "Delete", () =>
                runBulk(bulkDelete), true
              )
            }
          />
          <Button
            label="Clear"
            className="p-button-text p-button-sm"
            onClick={() => setSelected([])}
          />
        </div>
      )}

      {/*
        loading is a prop rather than a spinner swapped in for the table: every row action
        refetches, and unmounting the DataTable each time would throw away its sort order and
        scroll position.
      */}
      <DataTable
        value={visibleCertificates}
        loading={isLoading}
        dataKey="thumbprint"
        size="small"
        scrollable
        scrollHeight="flex"
        emptyMessage="No application certificates."
        filters={filters}
        onFilter={(e) => setFilters(e.filters)}
        globalFilterFields={["subject", "thumbprint", "kind"]}
        selection={selected}
        onSelectionChange={(e) => setSelected(e.value as OpcUaCertificateDTO[])}
        selectionMode="checkbox"
        style={{ flex: 1, minHeight: 0 }}
      >
        <Column selectionMode="multiple" style={{ width: "3rem" }} />
        <Column
          field="subject"
          header="Subject"
          sortable
          body={(certificate: OpcUaCertificateDTO) => (
            <span
              title={certificate.subject}
              className="block text-overflow-ellipsis overflow-hidden white-space-nowrap"
            >
              {certificate.subject || "—"}
            </span>
          )}
          style={{ maxWidth: "16rem" }}
        />
        <Column
          field="kind"
          header="Kind"
          sortable
          body={(certificate: OpcUaCertificateDTO) => (
            <Tag
              value={kindLabel(certificate.kind)}
              severity={kindSeverity(certificate.kind)}
            />
          )}
          style={{ width: "8rem" }}
        />
        <Column
          header="Thumbprint"
          body={(certificate: OpcUaCertificateDTO) => (
            <ThumbprintCell thumbprint={certificate.thumbprint} />
          )}
          style={{ width: "15rem" }}
        />
        <Column
          field="enabled"
          header="Enabled"
          sortable
          body={(certificate: OpcUaCertificateDTO) => (
            <EnabledTag enabled={certificate.enabled} />
          )}
          style={{ width: "7rem" }}
        />
        <Column
          field="createdAt"
          header="Created"
          sortable
          body={(certificate: OpcUaCertificateDTO) =>
            formatCreatedAt(certificate.createdAt)
          }
          style={{ width: "12rem" }}
        />
        <Column body={actionBodyTemplate} style={{ width: "13rem" }} />
      </DataTable>

      {detailsCertificate && (
        <CertificateDetailsDialog
          certificate={detailsCertificate}
          onDownload={downloadCertificate}
          close={() => setDetailsCertificate(null)}
        />
      )}
    </div>
  );
};

export default ApplicationCertificatesTab;
